import React from 'react';
import { t } from '@lingui/core/macro';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { Block } from '../classes/Block';
import './CopyFormattingDialog.css';

export type FormattingGroup = 'layout' | 'label' | 'value' | 'all';

interface CopyFormattingDialogProps {
  group: FormattingGroup | null;
  sourceBlockName: string;
  targetBlocks: Block[];
  selectedBlockIds: Set<string>;
  onToggleBlock: (blockId: string) => void;
  onToggleAll: () => void;
  onClose: () => void;
  onApply: () => void;
}

const getGroupLabel = (group: FormattingGroup | null): string => {
  if (group === 'layout') return t`Block Layout`;
  if (group === 'label') return t`Label Formatting`;
  if (group === 'value') return t`Value Formatting`;
  if (group === 'all') return t`Block Layout, Label, and Value Formatting`;
  return '';
};

const CopyFormattingDialog: React.FC<CopyFormattingDialogProps> = ({
  group,
  sourceBlockName,
  targetBlocks,
  selectedBlockIds,
  onToggleBlock,
  onToggleAll,
  onClose,
  onApply,
}) => {
  const allSelected: boolean = selectedBlockIds.size === targetBlocks.length && targetBlocks.length > 0;
  const someSelected: boolean = selectedBlockIds.size > 0 && selectedBlockIds.size < targetBlocks.length;

  return (
    <Dialog open={group !== null} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t`Copy to Other Blocks`}</DialogTitle>
      <DialogContent className="copy-formatting-dialog">
        <p className="copy-formatting-dialog-lead">
          {`${getGroupLabel(group)}: ${sourceBlockName}`}
        </p>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={onToggleAll}
                color="primary"
              />
            }
            label={<strong>{allSelected ? t`Unselect All` : t`Select All`}</strong>}
          />
          <div className="copy-formatting-dialog-list">
            {targetBlocks.map((block: Block) => (
              <FormControlLabel
                key={block.id}
                control={
                  <Checkbox
                    checked={selectedBlockIds.has(block.id)}
                    onChange={() => onToggleBlock(block.id)}
                    color="primary"
                  />
                }
                label={block.name}
              />
            ))}
          </div>
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">{t`Cancel`}</Button>
        <Button
          onClick={onApply}
          variant="contained"
          disableElevation
          disabled={selectedBlockIds.size === 0}
        >
          {t`Apply`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyFormattingDialog;
